import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useUserRole } from '@/hooks/useUserRole';
import { Layout } from '@/components/Layout';
import { PageHeader } from '@/components/PageHeader';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Search, Trash2, ArrowLeft, Shield } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { mapSupabaseError } from '@/utils/security';

interface ProfileRow {
  id: string;
  user_id: string;
  display_name: string | null;
  email: string | null;
  account_type: string | null;
  is_verified: boolean | null;
  beta_access: boolean | null;
  setup_completed: boolean | null;
  avatar_url: string | null;
  bio: string | null;
  location: string | null;
  created_at: string | null;
  updated_at: string | null;
  // Talent-specific fields
  phone: string | null;
  talent_type: string | null;
  sport: string | null;
  team_name: string | null;
  athlete_level: string | null;
  instagram: string | null;
  tiktok: string | null;
  youtube: string | null;
  threads: string | null;
  x_handle: string | null;
  music_genre: string | null;
  label_status: string | null;
  label_name: string | null;
  entertainer_genres: string | null;
}

const AdminUsersPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAdmin, loading: roleLoading } = useUserRole();
  const [profiles, setProfiles] = useState<ProfileRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [subTypeFilter, setSubTypeFilter] = useState('all');
  const [deleting, setDeleting] = useState<string | null>(null);

  const fetchProfiles = async () => {
    setLoading(true);

    const [profilesRes, talentsRes] = await Promise.all([
      supabase
        .from('profiles')
        .select('id, user_id, display_name, email, account_type, is_verified, beta_access, setup_completed, avatar_url, bio, location, created_at, updated_at')
        .order('created_at', { ascending: false }),
      supabase
        .from('talents')
        .select('profile_id, talent_type, sport, team_name, competition_level, social_profiles, stats, cell_phone, name'),
    ]);

    if (profilesRes.error) {
      console.error('Error fetching profiles:', profilesRes.error);
      toast({ title: 'Error', description: 'Failed to load users', variant: 'destructive' });
      setLoading(false);
      return;
    }

    const talentMap = new Map<string, any>();
    if (talentsRes.data) {
      for (const t of talentsRes.data) {
        if (t.profile_id) talentMap.set(t.profile_id, t);
      }
    }

    const enriched: ProfileRow[] = (profilesRes.data || []).map(p => {
      const t = talentMap.get(p.id);
      const socials = (t?.social_profiles as any) || {};
      const stats = (t?.stats as any) || {};
      return {
        ...p,
        phone: t?.cell_phone || null,
        talent_type: t?.talent_type || null,
        sport: t?.sport || null,
        team_name: t?.team_name || null,
        athlete_level: t?.competition_level || null,
        instagram: socials.instagram || null,
        tiktok: socials.tiktok || null,
        youtube: socials.youtube || null,
        threads: socials.threads || null,
        x_handle: socials.x_handle || socials.twitter || null,
        music_genre: stats.music_genre || null,
        label_status: stats.label_status || null,
        label_name: stats.label_name || null,
        entertainer_genres: Array.isArray(stats.entertainer_genres)
          ? stats.entertainer_genres.join(', ')
          : stats.entertainer_genres || null,
      };
    });

    setProfiles(enriched);
    setLoading(false);
  };

  useEffect(() => {
    if (!roleLoading && isAdmin) fetchProfiles();
  }, [roleLoading, isAdmin]);

  const filtered = useMemo(() => {
    return profiles.filter(p => {
      const q = search.toLowerCase();
      const matchesSearch = !q ||
        (p.display_name || '').toLowerCase().includes(q) ||
        (p.email || '').toLowerCase().includes(q) ||
        (p.id || '').toLowerCase().includes(q) ||
        (p.sport || '').toLowerCase().includes(q) ||
        (p.team_name || '').toLowerCase().includes(q) ||
        (p.instagram || '').toLowerCase().includes(q) ||
        (p.tiktok || '').toLowerCase().includes(q) ||
        (p.youtube || '').toLowerCase().includes(q);
      const matchesType = typeFilter === 'all' || p.account_type === typeFilter;
      const matchesSubType = subTypeFilter === 'all' || p.talent_type === subTypeFilter;
      return matchesSearch && matchesType && matchesSubType;
    });
  }, [profiles, search, typeFilter, subTypeFilter]);

  const handleDelete = async (userId: string, displayName: string | null) => {
    setDeleting(userId);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const { data, error } = await supabase.functions.invoke('admin-delete-user', {
        body: { user_id: userId },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast({ title: 'User deleted', description: `${displayName || 'User'} has been removed.` });
      setProfiles(prev => prev.filter(p => p.user_id !== userId));
    } catch (err: any) {
      toast({ title: 'Error', description: mapSupabaseError(err, 'Failed to delete user'), variant: 'destructive' });
    } finally {
      setDeleting(null);
    }
  };

  if (roleLoading) {
    return (
      <Layout>
        <div className="p-6 space-y-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-64 w-full" />
        </div>
      </Layout>
    );
  }

  if (!isAdmin) {
    return (
      <Layout>
        <div className="p-6 text-center">
          <Shield className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold">Access Denied</h2>
          <p className="text-muted-foreground mt-2">Admin role required.</p>
        </div>
      </Layout>
    );
  }

  const accountTypes = [...new Set(profiles.map(p => p.account_type).filter(Boolean))];
  const talentSubTypes = [...new Set(profiles.map(p => p.talent_type).filter(Boolean))];

  const cell = (val: string | null) => (
    <span className="text-sm whitespace-nowrap">{val || '—'}</span>
  );

  return (
    <Layout>
      <PageHeader title="Active Users" subtitle={`${profiles.length} total users on the platform`} />

      <div className="p-4 md:p-6 space-y-6 max-w-[1600px] mx-auto">
        <Button variant="ghost" size="sm" onClick={() => navigate('/profile')} className="gap-1">
          <ArrowLeft className="h-4 w-4" /> Back to Profile
        </Button>

        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, email, sport, team, handle..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Account type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {accountTypes.map(t => (
                <SelectItem key={t} value={t!}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={subTypeFilter} onValueChange={setSubTypeFilter}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Sub-type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sub-types</SelectItem>
              {talentSubTypes.map(t => (
                <SelectItem key={t} value={t!} className="capitalize">{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="text-sm text-muted-foreground">
          Showing {filtered.length} of {profiles.length} users
        </div>

        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : (
          <div className="border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="sticky left-0 bg-background z-10">User</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Sub-type</TableHead>
                  <TableHead>Sport</TableHead>
                  <TableHead>Team</TableHead>
                  <TableHead>Level</TableHead>
                  <TableHead>Instagram</TableHead>
                  <TableHead>TikTok</TableHead>
                  <TableHead>YouTube</TableHead>
                  <TableHead>Threads</TableHead>
                  <TableHead>X</TableHead>
                  <TableHead>Music Genre</TableHead>
                  <TableHead>Label Status</TableHead>
                  <TableHead>Entertainer Genres</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead className="w-[60px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={20} className="text-center py-8 text-muted-foreground">
                      No users found
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map(p => (
                    <TableRow key={p.id}>
                      <TableCell className="sticky left-0 bg-background z-10">
                        <div className="flex items-center gap-2">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={p.avatar_url || undefined} />
                            <AvatarFallback className="text-xs">
                              {(p.display_name || '?')[0]?.toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div className="min-w-0">
                            <div className="font-medium truncate max-w-[140px]">{p.display_name || '—'}</div>
                            <div className="text-xs text-muted-foreground truncate max-w-[140px]">{p.id.slice(0, 8)}…</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{cell(p.email)}</TableCell>
                      <TableCell>{cell(p.phone)}</TableCell>
                      <TableCell>
                        {p.account_type ? (
                          <Badge variant="secondary" className="capitalize whitespace-nowrap">{p.account_type}</Badge>
                        ) : cell(null)}
                      </TableCell>
                      <TableCell>
                        {p.talent_type ? (
                          <Badge variant="outline" className="capitalize whitespace-nowrap">{p.talent_type}</Badge>
                        ) : cell(null)}
                      </TableCell>
                      <TableCell>{cell(p.sport)}</TableCell>
                      <TableCell>{cell(p.team_name)}</TableCell>
                      <TableCell>{cell(p.athlete_level)}</TableCell>
                      <TableCell>{cell(p.instagram)}</TableCell>
                      <TableCell>{cell(p.tiktok)}</TableCell>
                      <TableCell>{cell(p.youtube)}</TableCell>
                      <TableCell>{cell(p.threads)}</TableCell>
                      <TableCell>{cell(p.x_handle)}</TableCell>
                      <TableCell>{cell(p.music_genre)}</TableCell>
                      <TableCell>{cell(p.label_status)}</TableCell>
                      <TableCell>{cell(p.entertainer_genres)}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {p.is_verified && <Badge variant="default" className="text-[10px] px-1.5 whitespace-nowrap">Verified</Badge>}
                          {p.beta_access && <Badge variant="outline" className="text-[10px] px-1.5 whitespace-nowrap">Beta</Badge>}
                          {p.setup_completed && <Badge variant="outline" className="text-[10px] px-1.5 whitespace-nowrap">Setup ✓</Badge>}
                        </div>
                      </TableCell>
                      <TableCell>{cell(p.location)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {p.created_at ? format(new Date(p.created_at), 'MMM d, yyyy') : '—'}
                      </TableCell>
                      <TableCell>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                              disabled={deleting === p.user_id}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete User</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will permanently delete <strong>{p.display_name || p.email || 'this user'}</strong> and all their data. This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDelete(p.user_id, p.display_name)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminUsersPage;
